import { useEffect, useRef, useState } from 'react';

import type {
  ResearchPollFormat,
  ResearchQuestionShape,
  ResearchQuestionShapeResult,
} from '@repo/contracts';

import { classifyQuestion } from '../api/research-rooms';

/** Wait this long after the last keystroke before asking. Short: the word
 *  count already keeps half-typed questions out, and Jev takes a second to
 *  answer, so each word's reading starts while the next is typed. */
const DEBOUNCE_MS = 150;
/** Below this, nothing shows. A suggestion interrupts, so it waits for a
 *  reading Jev is fairly sure of. */
const HINT_AT = 0.6;
/** Above this, the composer takes the shape on its own. */
const COMMIT_AT = 0.7;
/** A different shape this sure takes over at once; otherwise it has to win
 *  twice in a row, so a card does not flicker with every word. */
const TAKEOVER_AT = 0.85;
/** Fewer words than this say too little to judge — "would you assume" is
 *  the start of a question, not one. */
const MIN_WORDS = 6;
/** A question mark says the question is finished, so it may be judged
 *  shorter. */
const MIN_WORDS_ASKED = 4;

/** Answers already had, shared by every composer — typing, deleting and
 *  retyping the same words asks once. */
const cache = new Map<string, ResearchQuestionShapeResult>();

function normalise(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** The cache key: closing punctuation does not change what is asked, so
 *  typing the "?" reuses the reading of the words before it. */
function cacheKey(text: string): string {
  return normalise(text).replace(/[?.!\s]+$/, '');
}

function top(result: ResearchQuestionShapeResult): {
  p: number;
  shape: ResearchQuestionShape;
} {
  let best: { p: number; shape: ResearchQuestionShape } = {
    p: 0,
    shape: 'open',
  };
  for (const [shape, p] of Object.entries(result.probabilities)) {
    if ((p ?? 0) > best.p) {
      best = { p: p ?? 0, shape: shape as ResearchQuestionShape };
    }
  }
  return best;
}

/** Whether the text reads as a whole question yet, worth asking Jev about. */
export function readyToJudge(text: string): boolean {
  const key = normalise(text);
  if (!key) return false;
  const words = key.split(' ').length;
  return words >= (key.endsWith('?') ? MIN_WORDS_ASKED : MIN_WORDS);
}

export interface QuestionShape {
  /** The poll format the composer should hold, once it is sure enough. */
  committed: ResearchPollFormat | null;
  /** A shape worth suggesting but not taking — "ask as a poll?". */
  hint: ResearchPollFormat | null;
  /** A reading of the current text is still on its way. */
  pending: boolean;
}

interface Hold {
  committed: ResearchPollFormat | null;
  challenger: ResearchQuestionShape | null;
  streak: number;
}

/**
 * The next held shape, given a new reading. The ShapeshiftUI rule, roughly:
 * commit above 0.7; once committed, a challenger has to win twice in a row or
 * be very sure to take over. Yes/no included: when Jev reads a question as
 * one, it is asked as one — the room answers with a vote and a reason each,
 * and nobody has to build the poll by hand.
 */
export function nextHold(
  hold: Hold,
  reading: { p: number; shape: ResearchQuestionShape },
): Hold {
  const committable =
    reading.shape !== 'open' && reading.p > COMMIT_AT ? reading.shape : null;

  if (hold.committed === null) {
    return { challenger: null, committed: committable, streak: 0 };
  }
  if (reading.shape === hold.committed) {
    return { ...hold, challenger: null, streak: 0 };
  }
  // A different shape — or a plain question — is contesting the card.
  const next: ResearchPollFormat | null =
    reading.shape === 'open' ? null : reading.shape;
  const streak = hold.challenger === reading.shape ? hold.streak + 1 : 1;
  if (reading.p > TAKEOVER_AT || (reading.p > COMMIT_AT && streak >= 2)) {
    return { challenger: null, committed: next, streak: 0 };
  }
  return { ...hold, challenger: reading.shape, streak };
}

/**
 * What kind of question is being typed, read by Jev on each pause.
 *
 * Debounced, cached, and sequence-numbered, so an answer to text that has
 * since changed is dropped rather than shown. A failed request changes
 * nothing — the composer simply stays what it is.
 */
export function useQuestionShape(
  text: string,
  enabled: boolean,
): QuestionShape {
  const [hold, setHold] = useState<Hold>({
    challenger: null,
    committed: null,
    streak: 0,
  });
  const [hint, setHint] = useState<ResearchPollFormat | null>(null);
  const [pending, setPending] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const key = cacheKey(text);
    if (!enabled || !readyToJudge(text)) {
      seq.current += 1;
      setHint(null);
      setPending(false);
      // Switched off (the researcher said "plain question") or emptied: the
      // last reading was about other words, and must not decide the next.
      if (!enabled || !key) {
        setHold({ challenger: null, committed: null, streak: 0 });
      }
      return;
    }

    const apply = (result: ResearchQuestionShapeResult) => {
      const reading = top(result);
      setHold((current) => nextHold(current, reading));
      setHint(
        reading.shape !== 'open' && reading.p >= HINT_AT ? reading.shape : null,
      );
    };

    const cached = cache.get(key);
    if (cached) {
      apply(cached);
      setPending(false);
      return;
    }

    // Not aborted when the text moves on: the sequence number already drops
    // a stale answer, and it is still worth caching for the next time.
    const mine = ++seq.current;
    // A question mark means the question is done: ask now.
    const delay = normalise(text).endsWith('?') ? 0 : DEBOUNCE_MS;
    setPending(true);
    const timer = setTimeout(() => {
      classifyQuestion(text).then(
        (result) => {
          cache.set(key, result);
          if (cache.size > 300) {
            const oldest = cache.keys().next().value;
            if (oldest !== undefined) cache.delete(oldest);
          }
          if (mine === seq.current) {
            apply(result);
            setPending(false);
          }
        },
        () => {
          if (mine === seq.current) setPending(false);
        },
      );
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [enabled, text]);

  return { committed: hold.committed, hint, pending };
}
